import { wheelUrls } from 'assets/data/config';
import type { FetchContext, FetchResponse } from 'ofetch';
import type {
  IPlayerTag,
  IPromotionGenericResponse,
  IPromotionGenericWheel,
  IPromotionSetReminder,
  IPromotionsResponse,
  TPromotionPageType,
  TPromotionPageVertical,
} from '~/interfaces/dto/promotions/response';
import type {
  IWelcomeOfferPlayerStatusResponse,
  IWelcomeOfferStepsResponse,
  IWelcomeOfferClaimPrize,
} from '~/interfaces/dto/welcome-offers/response';
import type {
  ICouponActivationResponse,
  ICouponGenericResponse,
  ICouponGetAll,
  IGlobalCoupons,
  IPlayerCoupons,
} from '~/interfaces/dto/coupons/response';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';

export const usePromotionService = () => {
  const { $fetch } = useNuxtApp();

  const { getLocale } = useLocaleUtils();
  const authStore = useAuthStore();
  const site = useSiteStore();

  const getWheelCount = async (): Promise<IPromotionGenericWheel> => {
    if (!authStore.access_token)
      return {
        success: true,
        data: [
          {
            promotionId: 0,
            unusedSpinCount: 0,
          },
        ],
        message: 'string',
        statusCode: 0,
        errors: ['string'],
      };
    return await $fetch<IPromotionGenericWheel>(
      `api/v1/GenericWheel/GetUserSpinSummary`,
      {
        base: 'promotions',
        useAuthentication: true,
        useBrandId: true,
        onResponse(
          context: FetchContext & { response: FetchResponse<ResponseType> },
        ): Promise<void> | void {
          authStore.setAvailableSpins(context.response._data.data);
        },
      },
    );
  };
  const getWelcomeOfferWheelCount = async (): Promise<{
    spinCount: number;
  }> => {
    if (!authStore.access_token) return { spinCount: 0 };

    const response = {
      promotionId: 1,
      isActive: true,
      promotionName: 'Welcome Offer',
      redirectUrl: wheelUrls[site.getRegionCode],
      unusedSpinCount: 0,
    };

    return await $fetch<{
      spinCount: number;
    }>(`api/v1/JWCWheel/GetOptinSpinCount`, {
      base: 'promotions',
      useBrandId: true,
      useAuthentication: true,
      query: {
        promotionId: 1,
      },
      onResponse(
        context: FetchContext & { response: FetchResponse<ResponseType> },
      ): Promise<void> | void {
        authStore.setAvailableSpins([
          {
            ...response,
            unusedSpinCount: context.response._data.spinCount || 0,
          },
        ]);
      },
      onResponseError(): Promise<void> | void {
        authStore.setAvailableSpins([response]);
      },
    });
  };

  // TODO: replace with better functionality
  const getGenericWheelCount =
    site.getRegionCode !== 'JZA' ? getWelcomeOfferWheelCount : getWheelCount;

  const fetchPromotions = async ({
    promotionPageVertical,
    promotionPageType,
    effectiveDate,
    slugPromotionName,
  }: {
    promotionPageVertical?: TPromotionPageVertical;
    promotionPageType?: TPromotionPageType;
    effectiveDate?: string;
    slugPromotionName?: string;
  }): Promise<IPromotionsResponse[]> => {
    try {
      const query = {
        cultureCode: getLocale().split('-')[0],
        isLive: true,
        forceFetch: authStore.isLoggedIn,
        ...(!!promotionPageVertical && { promotionPageVertical }),
        ...(!!promotionPageType && { promotionPageType }),
        ...(!!effectiveDate && { effectiveDate }),
        ...(!!slugPromotionName && { slugPromotionName }),
      };
      return await $fetch<IPromotionsResponse[]>(
        `api/v2/Promotion/promotions/{region}`,
        {
          base: authStore.isLoggedIn ? 'base' : 'baseC',
          useRegionCode: true,
          useAuthentication: authStore.isLoggedIn,
          useCache: !authStore.isLoggedIn,
          query,
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const updatePlayerReminder = async ({
    reminderId,
    isPlayerReminderActive,
  }: IPromotionSetReminder): Promise<IPromotionGenericResponse[]> => {
    try {
      const request = {
        reminderId,
        isPlayerReminderActive,
      };

      return await $fetch(`api/v2/Promotion/notification/SetPlayerReminder`, {
        base: 'base',
        method: 'post',
        useAuthentication: true,
        useBrandId: true,
        useRegionCode: true,
        body: request,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const getPlayerTags = async (): Promise<IPlayerTag[]> => {
    try {
      return await $fetch<IPlayerTag[]>(
        `api/v2/Tagging/GetVuvuzelaAccountTags`,
        {
          base: 'base',
          useAuthentication: true,
          useBrandId: true,
          onResponseError({ response }) {
            console.error(response);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const addEditTag = async ({
    tagName,
    value,
  }: {
    tagName: string;
    value: string;
  }): Promise<IPromotionGenericResponse[]> => {
    try {
      const request = {
        ...(!!tagName && { tagName }),
        ...(!!value && { value }),
      };
      return await $fetch('api/v2/Tagging/AddOrEditTagOnVuvuzelaAccount', {
        method: 'post',
        base: 'base',
        useAuthentication: true,
        useBrandId: true,
        body: request,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getWelcomeOfferStatus =
    async (): Promise<IWelcomeOfferPlayerStatusResponse> => {
      try {
        return await $fetch<IWelcomeOfferPlayerStatusResponse>(
          `api/v2/WelcomeOffer`,
          {
            base: 'promotions',
            useAuthentication: true,
            useBrandId: true,
            onResponseError({ response }) {
              console.error(response);
            },
          },
        );
      } catch (error) {
        console.error(error);
      }
    };

  const updateWelcomeOfferStatus = async ({
    prizeNumber,
    prizeSelectionValue,
  }: IWelcomeOfferClaimPrize): Promise<IPromotionGenericResponse> => {
    try {
      const request = {
        ...(!!prizeNumber && { prizeNumber }),
        ...(!!prizeSelectionValue && { prizeSelectionValue })
      };
      return await $fetch<IPromotionGenericResponse>(`api/v2/WelcomeOffer`, {
        method: 'post',
        body: request,
        base: 'promotions',
        useAuthentication: true,
        useBrandId: true,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getWelcomeOfferSteps = async (): Promise<
    IWelcomeOfferStepsResponse[]
  > => {
    try {
      return await $fetch(`api/v2/WelcomeOffer/WelcomeOfferSteps`, {
        base: 'promotions',
        useAuthentication: true,
        useBrandId: true,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getWelcomeOfferPrizes = async (): Promise<
    IWelcomeOfferStepsResponse[]
  > => {
    try {
      return await $fetch<IWelcomeOfferStepsResponse[]>(
        `api/v2/WelcomeOffer/WelcomeOfferPrizes`,
        {
          base: 'promotions',
          useAuthentication: true,
          useBrandId: true,
          onResponseError({ response }) {
            console.error(response);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getAllCouponsV2 = async (): Promise<ICouponGetAll> => {
    try {
      return await $fetch<ICouponGetAll>(`api/v2/Coupons`, {
        base: 'base',
        useAuthentication: true,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getAllPlayerCoupons = async (): Promise<IPlayerCoupons[]> => {
    try {
      return await $fetch<IPlayerCoupons[]>(`api/v3/Coupons`, {
        base: 'base',
        useAuthentication: true,
        onResponseError({ response }) {
          console.error(response);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const getAllGlobalCoupons = async (
    regionCode: string,
  ): Promise<IGlobalCoupons[]> => {
    try {
      return await $fetch<IGlobalCoupons[]>(
        `api/v1/Coupons/GlobalCoupons/${regionCode}`,
        {
          base: 'baseC',
          useAuthentication: true,
          onResponseError({ response }) {
            console.error(response);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const activatePlayerCoupon = async (
    couponId: number,
  ): Promise<ICouponActivationResponse> => {
    try {
      const request = {
        ...(!!couponId && { couponId }),
      };
      return await $fetch<ICouponActivationResponse>(
        `api/v1/Coupons/ActivatePlayerCoupon`,
        {
          method: 'post',
          base: 'base',
          useAuthentication: true,
          body: request,
          onResponseError({ response }) {
            console.error(response);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const activateGlobalCoupon = async (
    couponId: number,
  ): Promise<ICouponActivationResponse> => {
    try {
      const request = {
        ...(!!couponId && { couponId }),
      };
      return await $fetch<ICouponActivationResponse>(
        `api/v2/Coupons/ActivateGlobalPlayerCoupon/${couponId}`,
        {
          method: 'post',
          base: 'base',
          useAuthentication: true,
          body: request,
          onResponseError({ response }) {
            console.error(response);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  return {
    getGenericWheelCount,
    getWelcomeOfferWheelCount,
    fetchPromotions,
    updatePlayerReminder,
    getPlayerTags,
    addEditTag,
    getWelcomeOfferStatus,
    getWelcomeOfferSteps,
    getWelcomeOfferPrizes,
    updateWelcomeOfferStatus,
    getAllCouponsV2,
    getAllPlayerCoupons,
    getAllGlobalCoupons,
    activatePlayerCoupon,
    activateGlobalCoupon,
  };
};
